import React, { useEffect, useRef, useState, useCallback } from 'react';
import { GeneratedMaze } from '../utils/mazeGenerator';
import { Direction } from '../types/maze';

const VEC: Record<Direction, { dx: number; dy: number }> = {
  up: { dx: 0, dy: -1 },
  down: { dx: 0, dy: 1 },
  left: { dx: -1, dy: 0 },
  right: { dx: 1, dy: 0 },
};

const CELL = 48;
const TRAVEL_PX_PER_MS = 2.4;
const MIN_TRAVEL_MS = 220;
const MAX_TRAVEL_MS = 1300;
const STEP_GAP = 22;

export interface ExitingPiece {
  id: number;
  moving: boolean;
}

interface Props {
  maze: GeneratedMaze;
  hintPieceId: number | null;
  shakePieceId: number | null;
  exitingPiece: ExitingPiece | null;
  onExitComplete?: (id: number) => void;
}

type Pt = { x: number; y: number };

function cellCenter(row: number, col: number): Pt {
  return { x: col * CELL + CELL / 2, y: row * CELL + CELL / 2 };
}

function buildFullPath(maze: GeneratedMaze, piece: GeneratedMaze['pieces'][number]) {
  const { dx, dy } = VEC[piece.direction];
  const ordered = [...piece.cells].reverse();
  const points = ordered.map((c) => cellCenter(c.row, c.col));

  const head = piece.headCell;
  let stepsToEdge: number;
  switch (piece.direction) {
    case 'up': stepsToEdge = Math.floor((head.row * CELL + CELL / 2) / STEP_GAP) + 1; break;
    case 'down': stepsToEdge = Math.floor(((maze.rows - head.row - 1) * CELL + CELL / 2) / STEP_GAP) + 1; break;
    case 'left': stepsToEdge = Math.floor((head.col * CELL + CELL / 2) / STEP_GAP) + 1; break;
    case 'right': stepsToEdge = Math.floor(((maze.cols - head.col - 1) * CELL + CELL / 2) / STEP_GAP) + 1; break;
  }
  const extraSteps = stepsToEdge + 1;

  const full = [...points];
  for (let i = 0; i < extraSteps; i++) {
    const last = full[full.length - 1];
    full.push({ x: last.x + dx * STEP_GAP, y: last.y + dy * STEP_GAP });
  }
  return { full, n: points.length, extraSteps };
}

function pointAt(full: Pt[], index: number): Pt {
  const clamped = Math.max(0, Math.min(full.length - 1, index));
  const lo = Math.floor(clamped);
  const hi = Math.min(full.length - 1, lo + 1);
  const frac = clamped - lo;
  const a = full[lo];
  const b = full[hi];
  return { x: a.x + (b.x - a.x) * frac, y: a.y + (b.y - a.y) * frac };
}

const easeInQuad = (t: number) => t * t;

const MazePieceLayer: React.FC<Props> = ({ maze, hintPieceId, shakePieceId, exitingPiece, onExitComplete }) => {
  const width = maze.cols * CELL;
  const height = maze.rows * CELL;

  const [travelT, setTravelT] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);

  const handleExitComplete = useCallback((id: number) => {
    onExitComplete?.(id);
  }, [onExitComplete]);

  useEffect(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    startRef.current = null;
    setTravelT(0);

    if (!exitingPiece || !exitingPiece.moving) return;
    const piece = maze.pieces[exitingPiece.id];
    if (!piece) return;

    const { n, extraSteps } = buildFullPath(maze, piece);
    const totalSteps = n - 1 + extraSteps;
    const durationMs = Math.min(MAX_TRAVEL_MS, Math.max(MIN_TRAVEL_MS, (totalSteps * CELL) / TRAVEL_PX_PER_MS));

    const tick = (now: number) => {
      if (startRef.current === null) startRef.current = now;
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / durationMs);
      setTravelT(t);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        handleExitComplete(exitingPiece.id);
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [exitingPiece, maze, handleExitComplete]);   // ← Fixed

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full" style={{ display: 'block', overflow: 'hidden' }}>
      {maze.pieces
        .filter((p) => p.active || exitingPiece?.id === p.id)
        .map((piece) => {
          const { dx, dy } = VEC[piece.direction];
          const isExiting = exitingPiece?.id === piece.id;
          const isMoving = isExiting && exitingPiece?.moving;
          const isHint = hintPieceId === piece.id;
          const isShake = shakePieceId === piece.id;
          const color = isExiting ? '#22c55e' : isShake ? '#f43f5e' : isHint ? '#eab308' : '#1e40af';

          let pathPoints: Pt[];
          if (isMoving) {
            const { full, n, extraSteps } = buildFullPath(maze, piece);
            const totalSteps = n - 1 + extraSteps;
            const t = easeInQuad(travelT) * totalSteps;
            pathPoints = Array.from({ length: n }, (_, i) => pointAt(full, i + t));
          } else {
            const ordered = [...piece.cells].reverse();
            pathPoints = ordered.map((c) => cellCenter(c.row, c.col));
          }

          const scale = CELL / 100;
          const head = pathPoints[pathPoints.length - 1];
          const tipX = head.x + dx * (40 * scale);
          const tipY = head.y + dy * (40 * scale);
          const baseX = head.x + dx * (17 * scale);
          const baseY = head.y + dy * (17 * scale);
          const px = -dy;
          const py = dx;
          const w = 14 * scale;

          const d = `M ${pathPoints[0].x} ${pathPoints[0].y} ` +
            pathPoints.slice(1).map((p) => `L ${p.x} ${p.y}`).join(' ') +
            ` L ${baseX} ${baseY}`;

          return (
            <g key={piece.id}>
              <path 
                d={d} 
                fill="none" 
                stroke={color} 
                strokeWidth={11.5 * scale} 
                strokeLinecap="round" 
                strokeLinejoin="round" 
              />
              <polygon
                points={`${baseX + px * w},${baseY + py * w} ${tipX},${tipY} ${baseX - px * w},${baseY - py * w}`}
                fill={color}
                stroke="#1e3a8a"
                strokeWidth={2 * scale}
                strokeLinejoin="round"
              />
            </g>
          );
        })}
    </svg>
  );
};

export default MazePieceLayer;